import { useEffect, useState } from 'react';
import type { SessionDirectoryResult } from '@borgee/plugin-sdk/session-directory';
import { getSessionDirectory, supportsSessionDirectory } from '../lib/api';
import { dshSessionKey, useDshSessionStore, type DshSessionScope } from '../stores/useDshSessionStore';
import { usePresenceStore } from '../stores/usePresenceStore';

type DirectoryState = { key: string; connectionKey: string; status: 'idle' | 'loading' | 'unsupported' | 'ready' | 'error'; result?: SessionDirectoryResult };

/** Directory membership comes from the endpoint; local activity only supplies presentation. */
export function useSessionDirectory(scope?: DshSessionScope) {
  const epoch = usePresenceStore(state => state.providerOutputEpoch);
  const outputMode = usePresenceStore(state => scope ? state.providerOutputModes[scope.agentId] : undefined);
  const bindingKeys = useDshSessionStore(state => Object.entries(state.sessions).filter(([entryKey, value]) => {
    const [account, guild, channel, agent, binding] = JSON.parse(entryKey);
    return scope && account === scope.accountId && guild === scope.guildId && channel === scope.channelId && agent === scope.agentId && binding && value.sessionControlBindingId === binding;
  }).map(([entryKey]) => entryKey).sort().join('|'));
  const defaultRevision = useDshSessionStore(state => scope ? state.defaultSelectionRevisions[dshSessionKey({ ...scope, bindingId: undefined })] : undefined);
  const [attempt, setAttempt] = useState(0);
  const connectionKey = JSON.stringify([scope?.accountId, scope?.guildId, scope?.channelId, scope?.agentId, epoch, outputMode, attempt]);
  const [state, setState] = useState<DirectoryState>({ key: '', connectionKey: '', status: 'loading' });
  const key = state.connectionKey === connectionKey && state.status === 'unsupported' ? state.key : JSON.stringify([connectionKey, bindingKeys, defaultRevision]);
  useEffect(() => {
    if (!scope) return;
    const controller = new AbortController();
    const expiresAt = Date.now() + 15000;
    const timer = setTimeout(() => { controller.abort(); setState({ key, connectionKey, status: 'error' }); }, 15000);
    void (async () => {
      try {
        const supported = await supportsSessionDirectory(scope.channelId, scope.agentId, controller.signal, expiresAt);
        if (controller.signal.aborted) return;
        if (!supported) { setState({ key, connectionKey, status: 'unsupported' }); return; }
        const result = await getSessionDirectory(scope.channelId, scope.agentId, controller.signal, expiresAt);
        if (!controller.signal.aborted) setState({ key, connectionKey, status: 'ready', result });
      } catch {
        if (!controller.signal.aborted) setState({ key, connectionKey, status: 'error' });
      } finally { clearTimeout(timer); }
    })();
    return () => { clearTimeout(timer); controller.abort(); };
  }, [key]);
  const current: DirectoryState = !scope ? { key, connectionKey, status: 'idle' } : state.key === key ? state : { key, connectionKey, status: 'loading' };
  return { ...current, retry: () => setAttempt(value => value + 1) };
}
